import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, GripVertical, Trash2, ArrowLeft } from 'lucide-react'

export default function KanbanBoardsPage() {
  const navigate = useNavigate()
  const [cards, setCards] = useState([
    { id: 1, title: 'Design Homepage', assignee: 'Sarah', stage: 'todo', priority: 'High' },
    { id: 2, title: 'API Integration', assignee: 'Mike', stage: 'todo', priority: 'Critical' },
    { id: 3, title: 'Authentication Flow', assignee: 'Alex', stage: 'inProgress', priority: 'High' },
    { id: 4, title: 'User Dashboard', assignee: 'Emma', stage: 'inProgress', priority: 'Medium' },
    { id: 5, title: 'Payment Integration', assignee: 'John', stage: 'review', priority: 'Critical' },
    { id: 6, title: 'Documentation', assignee: 'Sarah', stage: 'done', priority: 'Low' },
  ])

  const [showModal, setShowModal] = useState(false)
  const [formData, setFormData] = useState({ title: '', assignee: '', stage: 'todo', priority: 'Medium' })

  const stages = [
    { id: 'todo', title: 'To Do', color: 'bg-slate-700' },
    { id: 'inProgress', title: 'In Progress', color: 'bg-blue-700' },
    { id: 'review', title: 'In Review', color: 'bg-yellow-700' },
    { id: 'done', title: 'Done', color: 'bg-green-700' },
  ]

  const cardsByStage = useMemo(() => {
    const grouped = {}
    stages.forEach(s => grouped[s.id] = [])
    cards.forEach(c => grouped[c.stage].push(c))
    return grouped
  }, [cards])

  const handleAddCard = () => {
    setFormData({ title: '', assignee: '', stage: 'todo', priority: 'Medium' })
    setShowModal(true)
  }

  const handleSaveCard = () => {
    if (!formData.title) return
    setCards([...cards, { id: Date.now(), ...formData }])
    setShowModal(false)
  }

  const handleDeleteCard = (id) => {
    setCards(cards.filter(c => c.id !== id))
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'text-red-400'
      case 'High': return 'text-orange-400'
      case 'Medium': return 'text-yellow-400'
      case 'Low': return 'text-green-400'
      default: return 'text-slate-400'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/projects')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Project Management
        </button>
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Kanban Board</h1>
            <p className="text-slate-400">Manage workflow with visual task management</p>
          </div>
          <button onClick={handleAddCard} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold">
            <Plus size={20} /> Add Card
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {stages.map((stage) => (
            <div key={stage.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-4 min-h-96">
              <div className={`${stage.color} rounded px-3 py-2 mb-4`}>
                <h3 className="font-semibold text-white text-sm">{stage.title}</h3>
                <p className="text-xs text-slate-300">{cardsByStage[stage.id].length} tasks</p>
              </div>

              <div className="space-y-3">
                {cardsByStage[stage.id].map((card) => (
                  <div key={card.id} className="bg-slate-700/50 rounded-lg p-4 border border-blue-500/10 hover:border-blue-400/30 cursor-move group">
                    <div className="flex items-start gap-2 mb-3">
                      <GripVertical size={16} className="text-slate-500 mt-1 opacity-0 group-hover:opacity-100" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-white">{card.title}</h4>
                        <p className="text-xs text-slate-400 mt-1">{card.assignee}</p>
                      </div>
                      <button onClick={() => handleDeleteCard(card.id)} className="opacity-0 group-hover:opacity-100">
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className={`font-semibold ${getPriorityColor(card.priority)}`}>{card.priority}</span>
                      <span className="text-slate-400">#{card.id}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-md w-full mx-4">
              <h2 className="text-2xl font-bold text-white mb-6">Add Card</h2>
              <div className="space-y-4 mb-6">
                <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Card Title" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <input type="text" value={formData.assignee} onChange={(e) => setFormData({...formData, assignee: e.target.value})} placeholder="Assignee" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <select value={formData.stage} onChange={(e) => setFormData({...formData, stage: e.target.value})} className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                  <option value="todo">To Do</option>
                  <option value="inProgress">In Progress</option>
                  <option value="review">In Review</option>
                  <option value="done">Done</option>
                </select>
                <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                  <option>Critical</option>
                </select>
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowModal(false)} className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-semibold">Cancel</button>
                <button onClick={handleSaveCard} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold">Add Card</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
